import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { notification } from 'antd';
import { API_BASE_URL, WS_ENDPOINT_URL, MARKETER_BASE_PATH } from 'configs/AppConfig';
import { ensureWS, subscribe } from 'wsClient';
import service from 'auth/FetchInterceptor';
import { playNotificationSound } from 'utils/notificationSound';
import { enablePushForAdmin } from 'utils/pushFunction';

/**
 * Keeps a WS connection for field marketers:
 * - location requests
 * - chat toasts when on another page
 * - browser notifications when app is backgrounded
 * - ensures push subscription for true background alerts
 */
export default function MarketerWsListener() {
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const handlingRef = useRef(new Set());

  const isMarketer = Boolean(user?.marketerOnly || user?.role === 'marketer');

  useEffect(() => {
    if (!isMarketer || !token) return undefined;

    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    // Best-effort push subscribe so background chat alerts work
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) {
      enablePushForAdmin().catch(() => {});
    }

    const raw = String(token).replace(/^Bearer\s+/i, '').replace(/^"|"$/g, '');
    const base = (WS_ENDPOINT_URL || API_BASE_URL || '').replace(/\/api\/?$/, '');
    const wsUrl = WS_ENDPOINT_URL
      ? WS_ENDPOINT_URL
      : `${base.replace(/^http/, 'ws')}/ws/`;

    ensureWS(wsUrl, raw);

    const shareLocation = async (requestId) => {
      const key = String(requestId || 'free');
      if (handlingRef.current.has(key)) return;
      handlingRef.current.add(key);

      if (!navigator.geolocation) {
        handlingRef.current.delete(key);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            await service.post('/marketer/location/report', {
              requestId: requestId || undefined,
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
            });
          } catch (err) {
            console.error('[marketer] location report failed', err);
          } finally {
            handlingRef.current.delete(key);
          }
        },
        (err) => {
          console.error('[marketer] geolocation failed', err);
          handlingRef.current.delete(key);
        },
        { enableHighAccuracy: true, timeout: 20000, maximumAge: 10000 }
      );
    };

    const unsub = subscribe((ev) => {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (msg?.type === 'pong' || msg?.type === 'auth') return;

      if (msg?.event === 'location.request') {
        const requestId = msg.payload?.requestId;
        if (
          typeof Notification !== 'undefined' &&
          Notification.permission === 'granted' &&
          document.visibilityState === 'hidden'
        ) {
          new Notification(msg.payload?.title || 'Location requested', {
            body: msg.payload?.body || 'Open Field CRM to share your location',
            icon: '/logo192.png',
            data: { url: msg.payload?.url || MARKETER_BASE_PATH },
          });
        }
        shareLocation(requestId);
      }

      if (msg?.event === 'chat:message') {
        const payload = msg.payload || {};
        window.dispatchEvent(
          new CustomEvent('codeforce:chat-message', { detail: payload })
        );

        const sender =
          payload.message?.sender?.name ||
          payload.message?.sender?.username ||
          'Someone';
        const preview = (payload.message?.content || 'New chat message').substring(0, 120);
        const chatUrl = `${MARKETER_BASE_PATH}/chat${payload.roomId ? `?room=${payload.roomId}` : ''}`;
        const onChatPage = window.location.pathname.includes('/marketer/chat');

        try {
          playNotificationSound();
        } catch { /* ignore */ }

        if (!onChatPage) {
          notification.open({
            message: 'Chat received',
            description: `${sender}: ${preview}`,
            duration: 6,
            placement: 'topRight',
            onClick: () => {
              window.location.assign(chatUrl);
            },
          });
        }

        if (
          typeof Notification !== 'undefined' &&
          Notification.permission === 'granted' &&
          document.visibilityState === 'hidden'
        ) {
          const n = new Notification('Chat received', {
            body: `${sender}: ${preview}`,
            icon: '/logo192.png',
            tag: `chat-${payload.roomId || 'room'}`,
            data: { url: chatUrl },
          });
          n.onclick = () => {
            window.focus();
            window.location.assign(chatUrl);
            n.close();
          };
        }
      }
    });

    service
      .get('/marketer/location/pending')
      .then((res) => {
        const list = res?.data || [];
        list.forEach((r) => shareLocation(r._id));
      })
      .catch(() => {});

    return () => {
      unsub();
    };
  }, [isMarketer, token]);

  return null;
}
